// Resolves the option set for an enum field. Inline `field.options` are used
// directly; otherwise, if the field carries a `config.data_source_raw`
// descriptor and the host supplied a dataSourceResolver, options are fetched
// async with loading / error state. A stale resolution is ignored if the field
// changes (or the component unmounts) before it settles.

import { useEffect, useState } from 'react'
import type { CommonConfig, DynamicFormField, Option } from '../types'
import { useFieldContext } from '../rendererContext'
import { inlineOptions } from './optionUtils'

export interface ResolvedOptions {
  options: Option[]
  loading: boolean
  error: string | null
}

export function useResolvedOptions(field: DynamicFormField): ResolvedOptions {
  const { dataSourceResolver } = useFieldContext()
  const inline = inlineOptions(field)
  const descriptor = (field.config as CommonConfig | undefined)?.data_source_raw

  const [state, setState] = useState<ResolvedOptions>({
    options: inline ?? [],
    loading: false,
    error: null,
  })

  useEffect(() => {
    if (inline) {
      setState({ options: inline, loading: false, error: null })
      return
    }
    if (!descriptor || !dataSourceResolver) {
      setState({ options: [], loading: false, error: null })
      return
    }

    let cancelled = false
    setState((prev) => ({ ...prev, loading: true, error: null }))
    dataSourceResolver(descriptor, field)
      .then((options) => {
        if (!cancelled) setState({ options: options ?? [], loading: false, error: null })
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : String(err)
          setState({ options: [], loading: false, error: message })
        }
      })
    return () => {
      cancelled = true
    }
    // `field` is stable across renders (sourced from the definition prop).
  }, [field, inline, descriptor, dataSourceResolver])

  return state
}
